import {ChangeDetectionStrategy, Component, computed, effect, inject, input, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {catchError, debounceTime, switchMap} from 'rxjs/operators';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {AppointmentClash, pickerToWallClock} from '@xmedical/core';
import dayjs from 'dayjs/esm';
import {CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';

/**
 * Non-blocking warning shown on the appointment forms: queries the clash endpoint
 * (debounced) whenever the location / time changes and lists any existing appointments
 * that overlap this practitioner or location. Purely advisory — it never prevents save.
 *
 * Practitioner is fixed by the parent (the appointment's practitioner, or the booking's
 * default); this component only reads it. When the provider can't fetch clashes (e.g. the
 * organisation-portal) or the inputs aren't complete, it renders nothing.
 */
@Component({
  selector: 'app-appointment-clash-banner',
  templateUrl: './appointment-clash-banner.component.html',
  styleUrls: ['./appointment-clash-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule],
})
export class AppointmentClashBannerComponent {
  locationId = input<number | null>(null);
  practitionerId = input<number | null>(null);
  start = input<Date | string | null>(null);
  end = input<Date | string | null>(null);
  /** The appointment being edited, so it doesn't clash with itself. Omit on create. */
  excludeAppointmentId = input<number | null>(null);
  /**
   * Whether to surface clashes at the *same* location as this booking. When the form
   * is launched from the calendar grid the conflicting appointment is already on screen,
   * so a same-location warning is redundant noise — default off. Blind entry points
   * (no grid in view) can opt in with `[warnSameLocation]="true"`. Other-location
   * clashes are always shown: they're the ones you can't see from here.
   */
  warnSameLocation = input(false);
  /**
   * Whether the calendar is currently filtering appointments by type. When it is, a
   * same-location clash may be for a type that's hidden from the grid — so it isn't
   * "already on screen" and must still be warned about, even when `warnSameLocation`
   * is off. (The clash row carries no type, so we can't tell which clashes are hidden;
   * when any filter is active we surface same-location clashes rather than risk hiding
   * an invisible one.)
   */
  appointmentTypeFiltersActive = input(false);

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);

  readonly clashes = signal<AppointmentClash[]>([]);
  readonly expanded = signal(false);

  /**
   * The clashes actually shown. The endpoint returns this practitioner's overlapping
   * appointments across all locations; we (a) keep only this practitioner's (defensive —
   * we never warn about another practitioner's bookings), (b) drop duplicate rows for the
   * same appointment, and (c) hide same-location clashes unless `warnSameLocation` is on.
   */
  readonly visibleClashes = computed<AppointmentClash[]>(() => {
    const practitionerId = this.practitionerId();
    // Same-location clashes are hidden only when they're guaranteed visible on the grid:
    // not explicitly requested, and no type filter is active that could be hiding them.
    const hideSameLocation = !this.warnSameLocation() && !this.appointmentTypeFiltersActive();
    const seen = new Set<number>();
    return this.clashes().filter(clash => {
      if (practitionerId != null && clash.practitionerId !== practitionerId) return false;
      if (seen.has(clash.id)) return false;
      seen.add(clash.id);
      if (hideSameLocation && this.isSameLocation(clash)) return false;
      return true;
    });
  });

  // The clash query is location-independent (this practitioner, any location), so the
  // fetch doesn't depend on locationId — changing location only re-classifies the
  // existing results (see visibleClashes), it doesn't refetch.
  private readonly request = computed(() => ({
    practitionerId: this.practitionerId(),
    start: this.start(),
    end: this.end(),
    exclude: this.excludeAppointmentId(),
  }));

  constructor() {
    toObservable(this.request).pipe(
      debounceTime(400),
      switchMap(req => {
        const provider = this.dataProvider;
        // Need a fetch capability, a concrete practitioner, and a time range.
        if (!provider.findClashes || req.practitionerId == null || !req.start || !req.end) {
          return of<AppointmentClash[]>([]);
        }
        const start = pickerToWallClock(req.start) as unknown as string;
        const end = pickerToWallClock(req.end) as unknown as string;
        return provider.findClashes(req.practitionerId, start, end, req.exclude ?? undefined).pipe(
          // Advisory only — a failed clash check must never disrupt the form.
          catchError(() => of<AppointmentClash[]>([])),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe(clashes => this.clashes.set(clashes));

    // Collapse when there's nothing left to show (clashes cleared, or all of them
    // filtered out by the same-location toggle), so a stale list can't stay open.
    effect(() => {
      if (this.visibleClashes().length === 0) {
        this.expanded.set(false);
      }
    });
  }

  get hasClashes(): boolean {
    return this.visibleClashes().length > 0;
  }

  toggle(): void {
    this.expanded.update(open => !open);
  }

  /** A clash at the location this booking is going into (vs at another location). Drives the
   *  same-location suppression in visibleClashes; the location itself is shown in the summary. */
  isSameLocation(clash: AppointmentClash): boolean {
    return this.locationId() != null && clash.locationId === this.locationId();
  }

  /** Render a zone-less wall-clock clash as "Thu 25 Jun, 09:30 – 10:00". */
  clashTime(clash: AppointmentClash): string {
    const start = dayjs(clash.start);
    const day = start.format('ddd D MMM, HH:mm');
    return clash.end ? `${day} – ${dayjs(clash.end).format('HH:mm')}` : day;
  }

  /**
   * What the clashing appointment is, then its location. The title usually identifies the
   * booking (it's the patient's name for a patient appointment, or e.g. "Out of Office"
   * otherwise) — but title is nullable and an unedited one can be a bare label, so fall back
   * to the patient name when the title is missing, otherwise the row would lose who the
   * conflict is with. The practitioner is omitted (clashes are self-scoped, always the
   * current user); the time range is shown separately (clashTime).
   */
  clashSummary(clash: AppointmentClash): string {
    const label = clash.title?.trim() || clash.patientName?.trim() || '';
    // Location name normally conveys where the clash is. If it's missing, still flag an
    // other-location clash as "another location" so the cross-site warning isn't lost
    // (a same-location clash needs no location cue).
    const location = clash.locationName?.trim() || (this.isSameLocation(clash) ? '' : 'another location');
    return [label, location].filter(Boolean).join(' · ');
  }
}
